import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Table, Spin, Modal, Descriptions, Tooltip, Tag } from "antd";
import "bootstrap/dist/css/bootstrap.css";
import SidebarNav from "../sidebar";
import { Link } from "react-router-dom";
import { getCoinDcxOrderHistory } from "../../../service/api";
import { toast } from "react-toastify";
import moment from "moment";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import CustomPagination from "../Pagination/pagination";
import Select from "react-select";
import { sanitizeInput } from "../../../config/validation";
import { triggerDownload } from "../../../config/downloadUtils";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTs = (ts) => {
  if (!ts) return "-";
  const m = typeof ts === "string" ? moment(ts) : moment(Number(ts));
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : String(ts);
};

const getTs = (row) =>
  Number(row.timestamp || row.created_at || row.createdAt || row.time || row.order_timestamp || 0);

const getStatus = (row) =>
  String(row.status || row.order_status || row.trade_status || "FILLED").toUpperCase();

const getSide = (row) =>
  String(row.side || row.order_side || "").toUpperCase();

const getType = (row) =>
  String(row.order_type || row.type || "").toUpperCase();

const TIME_KEYS = ["timestamp", "created_at", "order_timestamp", "time", "createdAt", "updatedAt", "cancelledAt", "cancelAt"];

const STATUS_COLORS = {
  FILLED: "green",
  COMPLETED: "green",
  OPEN: "blue",
  CANCELLED: "red",
  CANCELED: "red",
  REJECTED: "red",
  PARTIALLY_FILLED: "orange",
  PENDING: "orange",
};

const StatusBadge = ({ row }) => {
  const val = getStatus(row);
  return <Tag color={STATUS_COLORS[val] || "default"}>{val}</Tag>;
};

const SideBadge = ({ row }) => {
  const val = getSide(row);
  if (!val) return <span>-</span>;
  return <Tag color={val === "BUY" ? "green" : val === "SELL" ? "red" : "default"}>{val}</Tag>;
};

const EXCHANGE_COLORS = { coindcx: "blue", kucoin: "purple" };

const ExchangeBadge = ({ row }) => {
  const val = String(row.exchange || "coindcx").toLowerCase();
  return <Tag color={EXCHANGE_COLORS[val] || "default"}>{val === "kucoin" ? "KuCoin" : "CoinDCX"}</Tag>;
};

const ALL_COLUMNS = [
  {
    key: "id",
    title: "Trade / Order ID",
    defaultVisible: true,
    render: (row) => {
      const raw = row.id || row.order_id;
      const id = raw != null ? String(raw) : "-";
      return (
        <Tooltip title={id}>
          <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
            {id !== "-" ? `${id.substring(0, 14)}...` : "-"}
          </span>
        </Tooltip>
      );
    },
  },
  {
    key: "exchange",
    title: "Exchange",
    defaultVisible: true,
    render: (row) => <ExchangeBadge row={row} />,
  },
  {
    key: "symbol",
    title: "Symbol",
    defaultVisible: true,
    render: (row) => <strong>{row.symbol || row.market || row.pair || "-"}</strong>,
  },
  {
    key: "side",
    title: "Side",
    defaultVisible: true,
    render: (row) => <SideBadge row={row} />,
  },
  {
    key: "type",
    title: "Type",
    defaultVisible: true,
    render: (row) => row.order_type || row.type || "-",
  },
  {
    key: "status",
    title: "Status",
    defaultVisible: true,
    render: (row) => <StatusBadge row={row} />,
  },
  {
    key: "quantity",
    title: "Quantity",
    defaultVisible: true,
    render: (row) =>
      row.quantity != null ? Number(row.quantity).toFixed(6) : "-",
  },
  {
    key: "price",
    title: "Price",
    defaultVisible: true,
    render: (row) =>
      row.price != null ? Number(row.price).toFixed(4) : "-",
  },
  {
    key: "fee_amount",
    title: "Fee",
    defaultVisible: false,
    render: (row) =>
      row.fee_amount != null ? Number(row.fee_amount).toFixed(6) : "-",
  },
  {
    key: "timestamp",
    title: "Time",
    defaultVisible: true,
    render: (row) => formatTs(getTs(row) || undefined),
  },
];

const statusOptions = [
  { label: "All Statuses", value: "ALL" },
  { label: "Filled", value: "FILLED" },
  { label: "Open", value: "OPEN" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Partially Filled", value: "PARTIALLY_FILLED" },
  { label: "Pending", value: "PENDING" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Completed", value: "COMPLETED" },
];

const sideOptions = [
  { label: "All Sides", value: "ALL" },
  { label: "Buy", value: "BUY" },
  { label: "Sell", value: "SELL" },
];

const orderTypeOptions = [
  { label: "All Types", value: "ALL" },
  { label: "Limit", value: "LIMIT" },
  { label: "Market", value: "MARKET" },
  { label: "Stop Limit", value: "STOP_LIMIT" },
  { label: "Stop Market", value: "STOP_MARKET" },
];

const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "40px",
    width: "100%",
    minWidth: "120px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    fontSize: "14px",
    padding: "0px 4px",
    backgroundColor: "#fff",
  }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  indicatorSeparator: () => ({ display: "none" }),
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BATCH_SIZE = 500;

const EXCHANGE_LABELS = { coindcx: "CoinDCX", kucoin: "KuCoin" };

const exchangeOptions = [
  { label: "All Exchanges", value: "ALL" },
  { label: "CoinDCX", value: "coindcx" },
  { label: "KuCoin", value: "kucoin" },
];

const DEFAULT_FILTERS = {
  status: { label: "All Statuses", value: "ALL" },
  side: { label: "All Sides", value: "ALL" },
  orderType: { label: "All Types", value: "ALL" },
  dateFrom: "",
  dateTo: "",
  minPrice: "",
  maxPrice: "",
};

// ─── Component ───────────────────────────────────────────────────────────────

const CoinDcxTrades = () => {
  // Server-side state
  const [symbolInput, setSymbolInput] = useState("");
  const [activeSymbol, setActiveSymbol] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  // Server-side exchange selector — drives which API is called
  const [activeExchange, setActiveExchange] = useState("ALL");
  const [selectedExchange, setSelectedExchange] = useState(exchangeOptions[0]);

  // Data state
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastId, setLastId] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Client-side filters
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

  // Modal
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Page state for client-side slice
  const [page, setPage] = useState(1);

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "coindcx-orders-list",
    allColumns: ALL_COLUMNS,
    defaultPageSize: 50,
    pageSizeOptions: [20, 50, 100, 200],
  });

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const doFetch = useCallback(
    (sym, sort, fromId, append, exchange = "ALL") => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      const params = { limit: BATCH_SIZE, sort };
      if (sym) params.symbol = sym.toUpperCase().trim();

      if (exchange === "kucoin") {
        // KuCoin uses page-based pagination; fromId carries the page number
        params.exchange = "kucoin";
        if (fromId) params.page = fromId;
      } else if (exchange === "coindcx") {
        params.exchange = "coindcx";
        if (fromId) params.from_id = fromId;
      } else {
        // "ALL" — backend defaults to CoinDCX history
        if (fromId) params.from_id = fromId;
      }

      getCoinDcxOrderHistory(params)
        .then((res) => {
          const batch = Array.isArray(res) ? res : res?.data || [];
          const sorted = [...batch].sort((a, b) => {
            const ta = getTs(a);
            const tb = getTs(b);
            return sort === "desc" ? tb - ta : ta - tb;
          });

          setAllData((prev) => (append ? [...prev, ...sorted] : sorted));

          if (exchange === "kucoin") {
            // For KuCoin: lastId = next page number as string
            const fetchedPage = fromId ? Number(fromId) : 1;
            setLastId(batch.length >= BATCH_SIZE ? String(fetchedPage + 1) : null);
          } else {
            // For CoinDCX: lastId = cursor (last order id)
            setLastId(
              sorted.length > 0
                ? String(sorted[sorted.length - 1].id || sorted[sorted.length - 1].order_id || "")
                : null
            );
          }
          setHasMore(batch.length >= BATCH_SIZE);
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || "Failed to fetch trade history", { toastId: 'coin-dcx-orders-fetch-error' });
        })
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    []
  );

  useEffect(() => {
    doFetch("", "desc", null, false, "ALL");
  }, [doFetch]);

  // ── Server-side actions ────────────────────────────────────────────────────

  const handleSearch = () => {
    setActiveSymbol(symbolInput);
    setAllData([]);
    setLastId(null);
    doFetch(symbolInput, sortDir, null, false, activeExchange);
  };

  const handleClearSymbol = () => {
    setSymbolInput("");
    setActiveSymbol("");
    setAllData([]);
    setLastId(null);
    doFetch("", sortDir, null, false, activeExchange);
  };

  const handleSortToggle = () => {
    const newSort = sortDir === "desc" ? "asc" : "desc";
    setSortDir(newSort);
    setAllData([]);
    setLastId(null);
    doFetch(activeSymbol, newSort, null, false, activeExchange);
  };

  const handleExchangeChange = (opt) => {
    const newExchange = opt.value;
    setSelectedExchange(opt);
    setActiveExchange(newExchange);
    setAllData([]);
    setLastId(null);
    doFetch(activeSymbol, sortDir, null, false, newExchange);
  };

  const handleLoadMore = () => {
    doFetch(activeSymbol, sortDir, lastId, true, activeExchange);
  };

  // ── Client-side filtering ──────────────────────────────────────────────────

  const displayData = useMemo(() => {
    let list = allData;

    if (filters.status?.value !== "ALL") {
      list = list.filter((r) => getStatus(r) === filters.status.value);
    }
    if (filters.side?.value !== "ALL") {
      list = list.filter((r) => getSide(r) === filters.side.value);
    }
    if (filters.orderType?.value !== "ALL") {
      list = list.filter((r) => getType(r).includes(filters.orderType.value));
    }
    if (filters.dateFrom) {
      const from = moment(filters.dateFrom).startOf("day").valueOf();
      list = list.filter((r) => getTs(r) >= from);
    }
    if (filters.dateTo) {
      const to = moment(filters.dateTo).endOf("day").valueOf();
      list = list.filter((r) => getTs(r) <= to);
    }
    if (filters.minPrice !== "") {
      const min = Number(filters.minPrice);
      list = list.filter((r) => r.price != null && Number(r.price) >= min);
    }
    if (filters.maxPrice !== "") {
      const max = Number(filters.maxPrice);
      list = list.filter((r) => r.price != null && Number(r.price) <= max);
    }

    return list;
  }, [allData, filters]);

  const setFilter = (key, val) => setFilters((prev) => ({ ...prev, [key]: val }));

  const resetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setSymbolInput("");
    setActiveSymbol("");
    setSelectedExchange(exchangeOptions[0]);
    setActiveExchange("ALL");
    setAllData([]);
    setLastId(null);
    doFetch("", sortDir, null, false, "ALL");
  };

  // Reset to page 1 when filters/page size/data change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, filters, allData.length]);

  const pagedData = useMemo(
    () => displayData.slice((page - 1) * pageSize, page * pageSize),
    [displayData, page, pageSize],
  );

  // ── Table columns ──────────────────────────────────────────────────────────

  const handleDownload = (format) => {
    if (!displayData?.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
    const cols = ALL_COLUMNS?.filter((c) => visibleColumnKeys?.includes(c?.key));
    triggerDownload(format, displayData, cols, "coindcx-trades");
  };

  const actionColumn = {
    key: "__action",
    title: "Action",
    fixed: "right",
    render: (_, row) => (
      <button
        className="btn btn-sm btn-primary"
        onClick={() => {
          setSelectedRow(row);
          setModalVisible(true);
        }}
      >
        View
      </button>
    ),
  };

  const columns = [...dynamicColumns, actionColumn];

  // ── Render ─────────────────────────────────────────────────────────────────

  const exchangeLabel = activeExchange !== "ALL"
    ? (EXCHANGE_LABELS[activeExchange] || activeExchange)
    : "All Exchanges";

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-7 col-auto">
                <h3 className="page-title">{exchangeLabel} Trade History</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/admin">Dashboard</Link></li>
                  <li className="breadcrumb-item active">{exchangeLabel} Trades</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stats / load info */}
          <div className="mb-2">
            <small className="text-muted">
              Loaded: <strong>{allData.length}</strong> trades from {exchangeLabel}
              {activeSymbol && <span className="badge bg-info ms-2">{activeSymbol}</span>}
              &nbsp;|&nbsp; Showing: <strong>{displayData.length}</strong> after filters
              {hasMore && (
                <span className="text-warning ms-2">· More records available on {exchangeLabel}</span>
              )}
            </small>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    Trades & Orders
                    {activeSymbol && <span className="badge bg-info ms-2">{activeSymbol}</span>}
                  </h4>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={handleSortToggle}
                      disabled={loading}
                    >
                      {sortDir === "desc" ? "⬇ Newest First" : "⬆ Oldest First"}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setAllData([]);
                        setLastId(null);
                        doFetch(activeSymbol, sortDir, null, false);
                      }}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Refresh"}
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={displayData.length}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                  />
                  {loading && allData.length === 0 ? (
                    <div className="text-center py-5">
                      <Spin size="large" />
                    </div>
                  ) : (
                    <>
                      <Table
                        columns={columns}
                        dataSource={pagedData}
                        rowKey={(r, i) => String(r.id || r.order_id || i)}
                        pagination={false}
                        loading={loading}
                        scroll={{ x: "max-content" }}
                        size="small"
                      />

                      {displayData.length > pageSize && (
                        <CustomPagination
                          key={`pg-${pageSize}`}
                          totalTabs={displayData.length}
                          tabsPerPage={pageSize}
                          handleChange={setPage}
                          className="display-flex"
                        />
                      )}

                      {hasMore && (
                        <div className="text-center py-3 border-top">
                          <button
                            className="btn btn-outline-primary"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                          >
                            {loadingMore ? (
                              <>
                                <Spin size="small" /> &nbsp;Loading next {BATCH_SIZE}...
                              </>
                            ) : (
                              `Load Next ${BATCH_SIZE} Records from ${exchangeLabel}`
                            )}
                          </button>
                          <p className="text-muted mt-1 mb-0" style={{ fontSize: 12 }}>
                            Currently loaded: {allData.length} records
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="list-layout__filters">
              <FilterPanel
                count={displayData.length}
                onClear={resetFilters}
              >
                <FilterPanel.Field label="Symbol">
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      type="text"
                      placeholder="e.g. BTCINR"
                      value={symbolInput}
                      onChange={(e) => setSymbolInput(sanitizeInput(e.target.value))}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSearch}
                      disabled={loading}
                    >
                      Apply
                    </button>
                    {activeSymbol && (
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleClearSymbol}
                        disabled={loading}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </FilterPanel.Field>

                <FilterPanel.Field label="Exchange">
                  <Select
                    options={exchangeOptions}
                    value={selectedExchange}
                    onChange={handleExchangeChange}
                    styles={customStyles}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Status">
                  <Select
                    options={statusOptions}
                    value={filters.status}
                    onChange={(opt) => setFilter("status", opt)}
                    styles={customStyles}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Side">
                  <Select
                    options={sideOptions}
                    value={filters.side}
                    onChange={(opt) => setFilter("side", opt)}
                    styles={customStyles}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Order Type">
                  <Select
                    options={orderTypeOptions}
                    value={filters.orderType}
                    onChange={(opt) => setFilter("orderType", opt)}
                    styles={customStyles}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Date From">
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilter("dateFrom", e.target.value)}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Date To">
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilter("dateTo", e.target.value)}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Min Price">
                  <input
                    type="number"
                    placeholder="0"
                    value={filters.minPrice}
                    onChange={(e) => setFilter("minPrice", e.target.value)}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Max Price">
                  <input
                    type="number"
                    placeholder="∞"
                    value={filters.maxPrice}
                    onChange={(e) => setFilter("maxPrice", e.target.value)}
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>

      {/* ── Detail Modal ── */}
      <Modal
        title={
          <span>
            Trade / Order Details
            {selectedRow && (selectedRow.id || selectedRow.order_id) && (
              <span style={{ fontSize: "11px", color: "#888", marginLeft: 8 }}>
                {String(selectedRow.id || selectedRow.order_id)}
              </span>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <button
            key="close"
            className="btn btn-sm btn-secondary"
            onClick={() => setModalVisible(false)}
          >
            Close
          </button>,
        ]}
        width={720}
      >
        {selectedRow && (
          <Descriptions bordered column={1} size="small">
            {Object.entries(selectedRow).map(([key, value]) => (
              <Descriptions.Item
                key={key}
                label={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              >
                {TIME_KEYS.includes(key)
                  ? formatTs(value)
                  : value != null
                  ? String(value)
                  : "-"}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default CoinDcxTrades;
